/**
 * useAppMode — read + update the app-mode slice.
 *
 * The pure reducer lives in `@shopista/shared/stores/app-mode` (CP3, no side
 * effects). The web glue here is intentionally thin: the mode is purely
 * location-derived and never persisted. A cold launch seeds `DEFAULT_APP_MODE`
 * and entry detection corrects it; the header switcher is a plain in-memory
 * deviation and pressing the logo recomputes the mode from GPS. Nothing is
 * written to storage — there is no manual override to protect and no persisted
 * preference to hydrate (T12.6 Reopen 2).
 */

import { useCallback } from 'react';
import { setMode, type AppMode } from '@shopista/shared/stores';
import { useRootStore } from './root-store';

export function useAppMode(): AppMode {
  return useRootStore((s) => s.appMode.mode);
}

/**
 * Set the mode in-memory. Used by both the header switcher (a temporary
 * deviation) and GPS detection (the location-derived default). No override flag,
 * no persistence — a later GPS fix always applies, and a re-launch or a logo
 * press recomputes the mode from location (T12.6 Reopen 2).
 */
export function useSetAppMode(): (mode: AppMode) => void {
  return useCallback((mode: AppMode) => {
    const state = useRootStore.getState();
    useRootStore.setState({ appMode: setMode(state.appMode, { mode }) });
  }, []);
}
